'use client'

import { useMemo, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'

import type { RockWallsPageContent } from '@/lib/content/defaults/rock-walls'
import type { ContextResponseData } from '@/lib/context-response'
import { leadValue } from '@/lib/lead-value'
import { localiseResolver } from '@/lib/schemas/localise-resolver'
import { PHONE_MESSAGE } from '@/lib/schemas/phone'
import {
  buildWallEnquiryBody,
  wallEnquirySchema,
  type WallEnquiryFormData,
} from '@/lib/wall-enquiry'
import { useCsrf } from '@/hooks/use-csrf'
import { Button } from '@/components/ui/button'
import { AltchaWidget, useAltcha } from '@/components/altcha-widget'
import { ContextResponse } from '@/components/context-response'
import { CsrfRetry } from '@/components/csrf-retry'
import { FieldError, FieldInput, FieldLabel } from '@/components/form-field'
import { JobAddressField } from '@/components/job-address-field'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const FORM = { form_name: 'wall_enquiry', form_id: 'rock-walls' } as const

function track(event: string, params: GtagEventParams) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('event', event, params)
}

export function WallEnquiryForm({
  content,
  drivewayPrice,
}: {
  content: RockWallsPageContent['enquireForm']
  drivewayPrice: number
}) {
  const { token: csrfToken } = useCsrf()
  const captcha = useAltcha()
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [response, setResponse] = useState<ContextResponseData | null>(null)
  const [started, setStarted] = useState(false)
  const inFlight = useRef(false)
  const v = content.validation
  const resolver = useMemo(
    () =>
      localiseResolver(zodResolver(wallEnquirySchema), {
        'Name is required.': v.nameRequired,
        'Name is too long.': v.nameTooLong,
        'Phone number is required.': v.phoneRequired,
        'Phone number is too long.': v.phoneTooLong,
        [PHONE_MESSAGE]: v.phoneInvalid,
        'Postcode is required.': v.postcodeRequired,
        'Postcode is too long.': v.postcodeTooLong,
        'Job address is too long.': v.jobAddressTooLong,
        'Wall length is required.': v.wallLengthRequired,
        'Keep it short.': v.wallLengthTooLong,
        'Keep the height short.': v.wallHeightTooLong,
      }),
    [v]
  )

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<WallEnquiryFormData>({
    resolver,
    mode: 'onTouched',
    shouldFocusError: true,
    delayError: 300,
    defaultValues: {
      name: '',
      phone: '',
      postcode: '',
      wallLength: '',
      wallHeight: '',
      jobAddress: '',
    },
  })

  const jobAddressValue = useWatch({ control, name: 'jobAddress' }) ?? ''

  const onSubmit = async (data: WallEnquiryFormData) => {
    if (inFlight.current || !csrfToken) return
    inFlight.current = true
    try {
      setStatus('submitting')
      setResponse(null)

      const payload = await captcha.solve()
      if (!payload) {
        setStatus('error')
        setResponse({ severity: 'danger', text: content.errorMessage })
        track('form_error', { ...FORM, error_reason: 'captcha' })
        return
      }

      let res: Response
      try {
        res = await fetch('/api/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
          body: JSON.stringify({ ...buildWallEnquiryBody(data), captchaPayload: payload, website }),
        })
      } catch {
        setStatus('error')
        setResponse({ severity: 'danger', text: content.errorMessage })
        captcha.reset()
        track('form_error', { ...FORM, error_reason: 'network' })
        return
      }
      const result = (await res.json().catch(() => ({}))) as Partial<ContextResponseData> & {
        error?: string
      }

      if (!res.ok) {
        setStatus('error')
        setResponse({
          severity: result.severity ?? 'danger',
          text: result.text ?? result.error ?? content.errorMessage,
        })
        captcha.reset()
        track('form_error', { ...FORM, error_reason: `http_${res.status}` })
        return
      }

      setStatus('success')
      setResponse({ severity: 'success', text: content.successMessage })
      reset()
      captcha.reset()
      track('generate_lead', {
        currency: 'AUD',
        value: leadValue('rock-walls', drivewayPrice),
        event_category: 'engagement',
        event_label: 'rock-walls',
      })
      track('form_submit', { ...FORM, service_type: 'rock-walls', postcode: data.postcode })
    } finally {
      inFlight.current = false
    }
  }

  return (
    <form
      id="enquire"
      className="scroll-mt-24 space-y-5"
      noValidate
      onFocus={() => {
        if (started) return
        setStarted(true)
        track('form_start', FORM)
      }}
      onSubmit={(e) =>
        handleSubmit(onSubmit, (invalid) =>
          track('form_error', {
            ...FORM,
            error_reason: `invalid:${Object.keys(invalid).sort().join(',')}`,
          })
        )(e)
      }
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="wall-name">{content.nameLabel}</FieldLabel>
          <FieldInput
            id="wall-name"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            enterKeyHint="next"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'wall-name-error' : undefined}
            disabled={status === 'submitting'}
            {...register('name')}
          />
          <FieldError id="wall-name-error" message={errors.name?.message} />
        </div>
        <div>
          <FieldLabel htmlFor="wall-phone">{content.phoneLabel}</FieldLabel>
          <FieldInput
            id="wall-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'wall-phone-error' : undefined}
            disabled={status === 'submitting'}
            {...register('phone')}
          />
          <FieldError id="wall-phone-error" message={errors.phone?.message} />
        </div>
        <JobAddressField
          id="wall-job-address"
          value={jobAddressValue}
          disabled={status === 'submitting'}
          onTyped={(value) => {
            setValue('jobAddress', value)
            setValue('placeId', undefined)
            setValue('lat', undefined)
            setValue('lng', undefined)
          }}
          onPicked={(picked) => {
            setValue('jobAddress', picked.jobAddress)
            setValue('placeId', picked.placeId)
            setValue('lat', picked.lat)
            setValue('lng', picked.lng)
            if (picked.postcode) setValue('postcode', picked.postcode, { shouldValidate: true })
          }}
        />
        <div>
          <FieldLabel htmlFor="wall-postcode">{content.postcodeLabel}</FieldLabel>
          <FieldInput
            id="wall-postcode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="postal-code"
            enterKeyHint="next"
            aria-invalid={!!errors.postcode}
            aria-describedby={errors.postcode ? 'wall-postcode-error' : undefined}
            disabled={status === 'submitting'}
            {...register('postcode')}
          />
          <FieldError id="wall-postcode-error" message={errors.postcode?.message} />
        </div>
        <div>
          <FieldLabel htmlFor="wall-length">{content.lengthLabel}</FieldLabel>
          <FieldInput
            id="wall-length"
            type="text"
            enterKeyHint="next"
            placeholder={content.lengthPlaceholder}
            aria-invalid={!!errors.wallLength}
            aria-describedby={errors.wallLength ? 'wall-length-error' : undefined}
            disabled={status === 'submitting'}
            {...register('wallLength')}
          />
          <FieldError id="wall-length-error" message={errors.wallLength?.message} />
        </div>
        <div>
          <FieldLabel htmlFor="wall-height">{content.heightLabel}</FieldLabel>
          <FieldInput
            id="wall-height"
            type="text"
            enterKeyHint="send"
            placeholder={content.heightPlaceholder}
            aria-invalid={!!errors.wallHeight}
            aria-describedby={errors.wallHeight ? 'wall-height-error' : undefined}
            disabled={status === 'submitting'}
            {...register('wallHeight')}
          />
          <FieldError id="wall-height-error" message={errors.wallHeight?.message} />
        </div>
      </div>

      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      <AltchaWidget captcha={captcha} />

      {response && (status === 'success' || status === 'error') && (
        <ContextResponse severity={response.severity} message={response.text} size="lg" />
      )}

      <CsrfRetry />

      <Button
        type="submit"
        size="lg"
        className="h-auto min-h-14 w-full px-6 py-3 text-xl tracking-wide sm:w-auto sm:px-8"
        disabled={status === 'submitting' || !csrfToken}
      >
        {status === 'submitting' ? content.sendingLabel : content.buttonLabel}
      </Button>
    </form>
  )
}
